import { signal } from "@preact/signals-core";
import {
  cartData,
  addToCart,
  updateCartItem,
  updateCartProperty,
  refreshCart,
  type LimitContext,
} from "./cart-store";
import type { ModalContext } from "./modal-store";

// ─── Constants ────────────────────────────────────────────────────────────────

const VOUCHER_TAGS = ["Voucher", "voucher", "voucher-code"];
const MERGE_TAG = "MergeVoucherCodeVariants";

/** Line item property that carries the encoded voucher codes. */
const VOUCHER_PROP = "_encoded-vouchers";

// AWS Lambda function URL pattern:
// https://{url-id}.lambda-url.{region}.on.aws[/]
// url-id: 32 lowercase alphanumeric characters; region: e.g. us-east-1
const LAMBDA_URL_PATTERN =
  /^https:\/\/[a-z0-9]{32}\.lambda-url\.[a-z0-9-]+\.on\.aws\/?$/;

const FALLBACK_DEV_URL =
  "https://tpuorwloi446mxs7pwl2svkiim0nsrwf.lambda-url.us-east-1.on.aws/";
const FALLBACK_PROD_URL =
  "https://2m4fkxdjkw5awnhlb5xgwkjgxe0ltyrt.lambda-url.us-east-1.on.aws";

// ─── State ────────────────────────────────────────────────────────────────────

/** Set when a voucher-code fetch fails; components surface a retry notice. */
export const voucherError = signal<boolean>(false);

// ─── Tag helpers ──────────────────────────────────────────────────────────────

export const isVoucherProductTags = (tags: string[] | undefined): boolean =>
  Array.isArray(tags) && VOUCHER_TAGS.some((t) => tags.includes(t));

export const shouldMergeVouchers = (tags: string[] | undefined): boolean =>
  Array.isArray(tags) && tags.includes(MERGE_TAG);

// ─── Lambda config ────────────────────────────────────────────────────────────

const isValidLambdaUrl = (url: unknown): url is string =>
  typeof url === "string" && LAMBDA_URL_PATTERN.test(url.trim());

/**
 * Resolve the voucher Lambda endpoint.
 * Priority: valid `window.voucherLambdaUrl` metafield → store-name fallback.
 * Returns `null` (and warns) when neither a valid URL nor a store name is
 * available, so callers abort instead of hitting an unknown endpoint.
 */
const resolveLambdaUrl = (): string | null => {
  const metafieldUrl = (window as any).voucherLambdaUrl;
  const storeName = (window as any).storeName;

  if (isValidLambdaUrl(metafieldUrl)) {
    return metafieldUrl.trim();
  }

  if (metafieldUrl) {
    console.warn(
      "[voucher] Invalid Lambda URL format in metafield voucher_lambda_url:",
      metafieldUrl
    );
  }

  if (typeof storeName === "string" && storeName.trim() !== "") {
    return storeName === "Knocking Develop"
      ? FALLBACK_DEV_URL
      : FALLBACK_PROD_URL;
  }

  console.warn(
    "[voucher] Lambda URL and store name unavailable — voucher feature used but not configured; aborting add-to-cart"
  );
  return null;
};

// ─── Fetch ────────────────────────────────────────────────────────────────────

/**
 * Fetch `qty` encoded voucher codes from the Lambda for a vendor/sku pair.
 * Returns the array of codes, or `null` on any failure (missing config,
 * network error, or an empty/invalid response).
 */
export const fetchVoucherCodes = async (
  vendor: string,
  sku: string,
  qty: number
): Promise<string[] | null> => {
  if (!vendor || !sku || !qty) {
    console.error(
      "[voucher] Missing vendor/sku/qty for voucher-code fetch — aborting"
    );
    return null;
  }

  const url = resolveLambdaUrl();
  if (!url) return null;

  try {
    const query = `vendor=${encodeURIComponent(vendor)}&sku=${encodeURIComponent(
      sku
    )}&qty=${encodeURIComponent(String(qty))}`;
    const res = await fetch(`${url}?${query}`);
    if (!res.ok) throw new Error(res.statusText);

    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error("Empty or invalid response from voucher service");
    }
    return data as string[];
  } catch (err) {
    console.error("[voucher] Error fetching voucher codes:", err);
    return null;
  }
};

// ─── Property parsing ─────────────────────────────────────────────────────────

/**
 * Read the voucher-codes array off a cart line item.
 * Shopify may echo the property back as a JSON string, so parse when needed.
 */
const parseVoucherProp = (raw: unknown): string[] | null => {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as string[]) : null;
    } catch {
      return null;
    }
  }
  return null;
};

/** Whether the cart currently holds a voucher line whose code count ≠ its quantity. */
export const hasVoucherCodesOutOfSync = (): boolean => {
  const items = cartData.value?.items ?? [];
  return items.some((item) => {
    const codes = parseVoucherProp(item.properties?.[VOUCHER_PROP]);
    return codes != null && codes.length !== item.quantity;
  });
};

// ─── Add-to-cart orchestration ────────────────────────────────────────────────

/**
 * Add a voucher product to the cart, fetching encoded codes first.
 *
 * - Merge mode: one line item carrying the full codes array. If the variant is
 *   already in the cart, its quantity is bumped and reconciliation tops up the
 *   codes (see {@link reconcileVoucherCodes}).
 * - Split mode: one qty-1 line item per code, each flagged `_hideQtySelector`.
 *
 * Returns `true` when the item(s) were added, `false` when the fetch failed or
 * the add was blocked (e.g. by a purchase limit).
 */
export const addVoucherProductToCart = async (
  context: ModalContext
): Promise<boolean> => {
  const variantId = context.variantId;
  if (!variantId) return false;

  const vendor = context.vendor ?? "";
  const sku = context.variantSku ?? "";
  const qty = context.quantity || 1;
  const merge = Boolean(context.mergeVouchers);
  const customization = context.customization ?? {};
  const limitContext: LimitContext | undefined =
    context.productId && context.vendor
      ? { productId: context.productId, vendor: context.vendor }
      : undefined;
  // Forwarded to executeCartAdd so each voucher line publishes its own
  // `kn_add_to_cart`, staying 1:1 with Shopify's per-line product_added_to_cart.
  const analytics = { source: context.source, productId: context.productId };

  voucherError.value = false;

  if (merge) {
    // If this variant is already a voucher line, bump its quantity and let
    // reconciliation fetch the additional codes (avoids a redundant fetch).
    const items = cartData.value?.items ?? [];
    const idx = items.findIndex(
      (item) =>
        item.variant_id === variantId &&
        item.properties?.[VOUCHER_PROP] != null
    );
    if (idx !== -1) {
      await updateCartItem(idx + 1, items[idx].quantity + qty);
      return true;
    }

    const codes = await fetchVoucherCodes(vendor, sku, qty);
    if (!codes) {
      voucherError.value = true;
      return false;
    }
    return await addToCart(
      variantId,
      qty,
      { ...customization, [VOUCHER_PROP]: codes },
      limitContext,
      analytics
    );
  }

  // Split mode — one line item per code.
  const codes = await fetchVoucherCodes(vendor, sku, qty);
  if (!codes) {
    voucherError.value = true;
    return false;
  }

  let anyAdded = false;
  for (const code of codes) {
    const added = await addToCart(
      variantId,
      1,
      { ...customization, [VOUCHER_PROP]: [code], _hideQtySelector: true },
      limitContext,
      analytics
    );
    if (!added) break;
    anyAdded = true;
  }
  return anyAdded;
};

// ─── Reconciliation ───────────────────────────────────────────────────────────

/**
 * Keep each voucher line's code count in step with its quantity (merge mode).
 * Trims extra codes when quantity drops; fetches more when it rises.
 * Called after cart quantity changes. Returns `true` if it changed the cart.
 */
export const reconcileVoucherCodes = async (): Promise<boolean> => {
  const cart = cartData.value;
  if (!cart) return false;

  let changesMade = 0;

  for (let i = 0; i < cart.items.length; i++) {
    const item = cart.items[i];
    const codes = parseVoucherProp(item.properties?.[VOUCHER_PROP]);
    if (codes == null) continue;

    const qty = item.quantity;
    if (codes.length === qty) continue;

    const line = i + 1;

    if (codes.length > qty) {
      await updateCartProperty(line, VOUCHER_PROP, codes.slice(0, qty), qty);
      changesMade++;
      continue;
    }

    // codes.length < qty → fetch the difference and append.
    const codesToAdd = qty - codes.length;
    const newCodes = await fetchVoucherCodes(item.vendor, item.sku, codesToAdd);
    const merged = newCodes ? [...codes, ...newCodes] : codes;
    await updateCartProperty(line, VOUCHER_PROP, merged, qty);
    changesMade++;
  }

  if (changesMade > 0) {
    await refreshCart();
    return true;
  }
  return false;
};
